// Staff Time Tracking Components
import StaffCheckIn from './components/staff-time/StaffCheckIn';
import StaffManagement from './components/staff-time/StaffManagement';
import StaffReports from './components/staff-time/StaffReports';
import LocationsManagement from './components/admin/LocationsManagement';
import StaffSchedule from './components/admin/StaffSchedule';
import DateMigrationTool from './components/admin/DateMigrationTool';
import BHDedupTool from './components/admin/BHDedupTool';
import IncidentReport from './components/incident/IncidentReport';
import { LocationProvider } from './contexts/LocationContext';

// Auth
import LoginForm from './components/auth/LoginForm';
import { AuthProvider } from './contexts/AuthContext';
import { StudentProvider } from './contexts/StudentContext';
import BHWaiverForm from './components/registration/BHWaiverForm';

// Driver Components
import DriverSelect from './components/driver/DriverSelect';
import DriverDashboard from './components/driver/DriverDashboard';
import DriverStatusManager from './components/staff/DriverStatusManager';
import WeeklyDriverAssignment from './components/student/WeeklyDriverAssignment';

// Staff Components
import StaffDashboard from './components/staff/StaffDashboard';
import TodaysRoster from './components/staff/TodaysRoster';
import AdminDashboardSidebar from './components/admin/AdminDashboardSidebar';
import CampEnrollmentManager from './components/admin/CampEnrollmentManager';

// Student Components
import AllStudents from './components/student/AllStudents';
import AddStudent from './components/student/AddStudent';
import EditStudent from './components/student/EditStudent';
import ArchivedStudents from './components/student/ArchivedStudents';
import PaymentTracking from './components/admin/PaymentTracking';


// Front Desk App 
import FrontDesk from './components/staff/FrontDesk';
import PauseForBreak from './components/admin/PauseForBreak';

import { useAuthContext } from './contexts/AuthContext';
import React, { useState, useEffect } from 'react';
import { onSnapshot, query, orderBy } from 'firebase/firestore';
import { db, collection, addDoc } from './services/firebase';

const PickupManagementApp = () => {
  const { isAuthenticated, isDriver, getDriverId, logout, isAdmin, isStaff } = useAuthContext();
  const [currentView, setCurrentView] = useState('driver-select');
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [driversLoading, setDriversLoading] = useState(true);
  const [allStudents, setAllStudents] = useState([]);
  const [editingStudent, setEditingStudent] = useState(null);
  const [isPublicRoute, setIsPublicRoute] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [settingsTab, setSettingsTab] = useState('overview');
  const [registrationData, setRegistrationData] = useState({
    studentFirstName: '',
    studentLastName: '',
    grade: '',
    dateOfBirth: '',
    gender: '',
    learningDisabilities: 'None',
    learningDisabilitiesOther: '',
    medicalConditionsType: 'None',
    medicalConditions: '',
    programDaysPerWeek: '',
    startDate: '',
    parentFirstName: '',
    parentLastName: '',
    parentEmail: '',
    parentPhone: '',
    relationshipToStudent: '',
    homeAddress: '',
    schoolName: '',
    schoolAddress: '',
    schoolPhone: '',
    dismissalTimeNormal: '15:15',
    dismissalTimeWednesday: '14:15',
    pickupDays: [],
    campStartDate: '',
    campEndDate: '',
    authorizedPickup: '',
    photoRelease: '',
    agreementAccepted: false,
    parentSignature: '',
    partyDate: '',
    partyTime: '13:00',
    numberOfChildren: 10,
    partyTheme: 'Science Workouts',
    photoReleaseParty: false
  });

  const filterRegistrationData = (data, programId) => {
    const baseFields = {
      studentFirstName: data.studentFirstName,
      studentLastName: data.studentLastName,
      grade: data.grade,
      dateOfBirth: data.dateOfBirth,
      gender: data.gender,
      learningDisabilities: data.learningDisabilities,
      learningDisabilitiesOther: data.learningDisabilitiesOther,
      medicalConditionsType: data.medicalConditionsType,
      medicalConditions: data.medicalConditions,
      parentFirstName: data.parentFirstName,
      parentLastName: data.parentLastName,
      parentEmail: data.parentEmail,
      parentPhone: data.parentPhone,
      relationshipToStudent: data.relationshipToStudent,
      homeAddress: data.homeAddress,
      authorizedPickup: data.authorizedPickup,
      photoRelease: data.photoRelease,
      agreementAccepted: data.agreementAccepted,
      parentSignature: data.parentSignature,
      programSelection: data.programSelection
    };

    if (programId === 'birthday-parties') {
      return {
        ...baseFields,
        partyDate: data.partyDate,
        partyTime: data.partyTime,
        numberOfChildren: data.numberOfChildren,
        partyTheme: data.partyTheme,
        photoReleaseParty: data.photoReleaseParty
      };
    }

    if (programId === 'full-day-camp' || programId === 'third-party-billing') {
      return {
        ...baseFields,
        campStartDate: data.campStartDate,
        campEndDate: data.campEndDate,
        programDaysPerWeek: data.programDaysPerWeek
      };
    }

    if (programId === 'after-school-pickup' || programId === 'after-school-dropoff') {
      return {
        ...baseFields,
        programDaysPerWeek: data.programDaysPerWeek,
        startDate: data.startDate,
        pickupDays: data.pickupDays,
        needsPickup: data.needsPickup,
        ...(data.needsPickup && {
          schoolName: data.schoolName,
          schoolAddress: data.schoolAddress,
          schoolPhone: data.schoolPhone,
          dismissalTimeNormal: data.dismissalTimeNormal,
          dismissalTimeWednesday: data.dismissalTimeWednesday
        })
      };
    }

    if (programId === 'saturday-classes') {
      return {
        ...baseFields,
        startDate: data.startDate
      };
    }

    return baseFields;
  };

  const programInfo = {  // ← This line stays here
    name: 'Stemtree of Redmond',
    address: '17634 NE Union Hill Rd, Redmond, WA 98052',
    phone: '(425) 559-7836',
    email: 'redmond@stemtree.com'
  };

  const stripePaymentLinks = {
    'after-school-pickup': {
      '1': 'https://buy.stripe.com/cNi3cvaqD75c381dBf3AY00',
      '2': 'https://buy.stripe.com/cNi5kDeGT9dk8slfJn3AY01',
      '3': 'https://buy.stripe.com/bJefZhdCP75c0ZT40F3AY02',
      '4': 'https://buy.stripe.com/8x26oHcyLdtAcIB7cR3AY03',
      '5': 'https://buy.stripe.com/cNifZh6an0GO23X1Sx3AY04'
    },
    'after-school-dropoff': {
      '1': 'https://buy.stripe.com/6oU3cv7er9dkeQJcxb3AY05',
      '2': 'https://buy.stripe.com/00wdR9gP1gFM6kd8gV3AY06',
      '3': 'https://buy.stripe.com/6oU4gzgP12OWaAtbt73AY07',
      '4': 'https://buy.stripe.com/eVq4gz1U70GO5g92WB3AY08',
      '5': 'https://buy.stripe.com/dRm00j6an75c9wpfJn3AY09'
    },
    'saturday-classes': {
      'monthly': 'https://buy.stripe.com/5kQ9ATgP12OWcIB0Ot3AY0a'
    },
    'full-day-camp': {
      '1-day': 'https://buy.stripe.com/3cIfZhdCPexE0ZTgNr3AY0b',
      '2-days': 'https://buy.stripe.com/6oUbJ156j9dk23XgNr3AY0c',
      '3-days': 'https://buy.stripe.com/7sY4gzeGT9dk8sl68N3AY0d',
      '4-days': 'https://buy.stripe.com/28EbJ1eGTahocIB1Sx3AY0e',
      '1-week': 'https://buy.stripe.com/dRm6oH42f2OW6kdeFj3AY0f',
      '2-weeks': 'https://buy.stripe.com/6oU7sLfKX1KScIBeFj3AY0g'
    },
  };

  // Load drivers from Firestore on app mount
  useEffect(() => {
    const loadDrivers = async () => {
      try {
        const { getAllDrivers } = await import('./services/driverService');
        const driversData = await getAllDrivers();
        setDrivers(driversData);
      } catch (error) {
        console.error('Error loading drivers:', error);
        setDrivers([]);
      } finally {
        setDriversLoading(false);
      }
    };
    
    loadDrivers();
  }, []);

  const styles = {
    header: {
      background: 'white',
      color: '#111827',
      padding: '1rem',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      borderBottom: '3px solid #00A3E0'
    },
    loginContainer: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #00A3E0 0%, #ee6123 100%)',
      border: 'none',
      padding: '20px'
    },
    loginBox: {
      backgroundColor: 'white',
      borderRadius: '16px',
      padding: '3rem',
      boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      maxWidth: '400px',
      width: '100%',
      textAlign: 'center'
    },
    loginTitle: {
      fontSize: '2rem',
      fontWeight: 'bold',
      color: '#1f2937',
      marginBottom: '0.5rem'
    },
    loginSubtitle: {
      color: '#6b7280',
      marginBottom: '2rem',
      fontSize: '0.95rem'
    },
    pinInput: {
      width: '100%',
      padding: '1rem',
      fontSize: '1.5rem',
      textAlign: 'center',
      border: '2px solid #e5e7eb',
      borderRadius: '12px',
      marginBottom: '1rem',
      letterSpacing: '0.5rem',
      fontWeight: 'bold',
      outline: 'none',
      transition: 'border-color 0.2s'
    },
    pinError: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#ef4444',
      backgroundColor: '#fee2e2',
      padding: '0.75rem',
      borderRadius: '8px',
      marginBottom: '1rem',
      fontSize: '0.875rem'
    },
    loginButton: {
      width: '100%',
      padding: '1rem',
      fontSize: '1.1rem',
      fontWeight: 'bold',
      color: 'white',
      background: 'linear-gradient(135deg, #00A3E0 0%, #ee6123 100%)',
      border: 'none',
      borderRadius: '12px',
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s',
      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
    },
    headerTitle: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      margin: '0 0 0.25rem 0',
      color: '#111827'
    },
    headerSubtitle: {
      fontSize: '0.875rem',
      margin: '0',
      color: '#6b7280'
    },
    backButton: {
      background: '#f3f4f6',
      border: '1px solid #d1d5db',
      color: '#374151',
      padding: '0.5rem 1rem',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '0.875rem',
      marginBottom: '1rem',
      fontWeight: '500'
    },
    card: {
      backgroundColor: 'white',
      padding: '1.5rem',
      margin: '1rem',
      borderRadius: '12px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      border: '1px solid #e5e7eb'
    },
    driverCard: {
      backgroundColor: 'white',
      padding: '2rem',
      margin: '1rem',
      borderRadius: '16px',
      boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
      border: '1px solid #e5e7eb',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      position: 'relative',
      overflow: 'hidden'
    },
    button: {
      backgroundColor: '#3b82f6',
      color: 'white',
      padding: '0.75rem 1.5rem',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      margin: '0.25rem',
      fontSize: '0.875rem',
      fontWeight: '600',
      transition: 'background-color 0.2s'
    },
    buttonGreen: {
      background: 'linear-gradient(135deg, #00A3E0 0%, #ee6123 100%)',
      color: 'white',
      padding: '0.75rem 1.5rem',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      margin: '0.25rem',
      fontSize: '0.875rem',
      fontWeight: '600'
    },
    buttonOrange: {
      backgroundColor: '#f59e0b',
      color: 'white',
      padding: '0.75rem 1.5rem',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      margin: '0.25rem',
      fontSize: '0.875rem',
      fontWeight: '600'
    },
    statsCard: {
      backgroundColor: 'white',
      padding: '1.5rem',
      borderRadius: '12px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      textAlign: 'center',
      margin: '0.5rem',
      border: '1px solid #e5e7eb'
    },
    badge: {
      padding: '0.25rem 0.75rem',
      borderRadius: '9999px',
      fontSize: '0.75rem',
      fontWeight: '600',
      margin: '0.125rem'
    },
    badgePending: {
      backgroundColor: '#fef3c7',
      color: '#92400e',
      border: '1px solid #fcd34d'
    },
    badgePickedUp: {
      backgroundColor: '#dbeafe',
      color: '#1e40af',
      border: '1px solid #60a5fa'
    },
    badgeDelivered: {
      backgroundColor: '#d1fae5',
      color: '#065f46',
      border: '1px solid #6ee7b7'
    },
    input: {
      width: '100%',
      padding: '0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.875rem',
      marginBottom: '1rem'
    },
    select: {
      width: '100%',
      padding: '0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.875rem',
      marginBottom: '1rem',
      backgroundColor: 'white'
    },
    textarea: {
      width: '100%',
      padding: '0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '0.875rem',
      marginBottom: '1rem',
      minHeight: '80px',
      resize: 'vertical'
    },
    gridTwoCols: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '1rem',
      marginBottom: '1rem'
    },
    flexBetween: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1rem'
    },
    centerContainer: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '1rem'
    },
    driverIcon: {
      width: '64px',
      height: '64px',
      background: 'linear-gradient(135deg, #00A3E0 0%, #ee6123 100%)',
      border: 'none',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 1rem auto',
      boxShadow: '0 4px 12px rgba(0, 163, 224, 0.3)'
    }
  };

  // Auto-navigate drivers to their dashboard
  useEffect(() => {
    if (isAuthenticated() && isDriver() && !driversLoading && drivers.length > 0) {
      const driverId = getDriverId();
      const driver = drivers.find(d => d.id === driverId);
      
      if (driver) {
        setSelectedDriver(driver);
        setCurrentView('driver-dashboard');
      }
    }
  }, [isAuthenticated, isDriver, getDriverId, drivers, driversLoading]);

useEffect(() => {
  const path = window.location.pathname;
  const searchParams = new URLSearchParams(window.location.search);
  
  if (path === '/register') {
    setIsPublicRoute(true);
    
    if (searchParams.get('success') === 'true') {
      setCurrentView('payment-success');
    } else {
      setCurrentView('public-register');
      
      // Check for BH pre-fill parameter
      const studentId = searchParams.get('id');
      if (studentId) {
        // Load student data from Firestore and pre-select BH program
        loadBHStudentData(studentId);
      }
    }
} else if (path === '/payment-success') {
      setIsPublicRoute(true);
      setCurrentView('payment-success');
    } else if (path === '/staff-checkin') {
      setIsPublicRoute(true);
      setCurrentView('staff-checkin');
    } else if (path === '/bh-waiver') {
      setIsPublicRoute(true);
      setCurrentView('bh-waiver');
      } else if (path === '/incident') {
  setIsPublicRoute(true);
  setCurrentView('incident-report');
    } else {
      setIsPublicRoute(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load BH student data if coming from email link
const loadBHStudentData = async (studentId) => {
  try {
    const { doc, getDoc } = await import('firebase/firestore');
    const studentDoc = await getDoc(doc(db, 'students', studentId));
    
    if (studentDoc.exists()) {
      const studentData = studentDoc.data();
      
      // Convert MM-DD-YYYY to yyyy-MM-dd for HTML date inputs
      const convertToDateInputFormat = (dateStr) => {
        if (!dateStr) return '';
        try {
          // Handle MM-DD-YYYY format
          if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
            const [month, day, year] = dateStr.split('-');
            return `${year}-${month}-${day}`;
          }
          // Handle other formats
          const date = new Date(dateStr);
          if (isNaN(date.getTime())) return '';
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const dayNum = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${dayNum}`;
        } catch {
          return '';
        }
      };
      
      // Auto-select BH program
      const bhProgram = {
        id: 'third-party-billing',
        name: 'Bright Horizons Registration Form Only',
        needsPickup: false
      };
      
      setSelectedProgram(bhProgram);
      
      // Pre-fill registration data with BH info
      setRegistrationData({
        ...registrationData,
        studentFirstName: studentData.firstName || '',
        studentLastName: studentData.lastName || '',
        dateOfBirth: convertToDateInputFormat(studentData.dateOfBirth),
        gender: studentData.gender || '',
        grade: studentData.grade || '',
        parentFirstName: studentData.parentName?.split(' ')[0] || '',
        parentLastName: studentData.parentName?.split(' ').slice(1).join(' ') || '',
        parentEmail: studentData.parentEmail || '',
        parentPhone: studentData.parentPhone || '',
        medicalConditionsType: studentData.medicalConditions !== 'None' ? 'Other' : 'None',
        medicalConditions: studentData.medicalConditions || '',
        campStartDate: convertToDateInputFormat(studentData.campStartDate),
        campEndDate: convertToDateInputFormat(studentData.campEndDate),
        homeAddress: studentData.homeAddress || '',
        authorizedPickup: studentData.authorizedPickup || '',
        // Store student ID and mark as pre-filled
        _bhStudentId: studentId,
        _bhPreFilled: true
      });
      
      // Go directly to registration form
      setCurrentView('registration-form');
    }
  } catch (error) {
    console.error('Error loading BH student data:', error);
  }
};

  // Check for BH pre-fill on mount
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const studentId = searchParams.get('id');
    
    if (studentId && currentView === 'public-register') {
      loadBHStudentData(studentId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView]);

  useEffect(() => {
    const studentsQuery = query(collection(db, 'students'), orderBy('submissionDate', 'desc'));
    
    const unsubscribe = onSnapshot(studentsQuery, (snapshot) => {
      const allStudentsList = [];
      
      snapshot.docs.forEach(doc => {
        const student = { id: doc.id, ...doc.data() };
        allStudentsList.push(student);
      });
      
      setAllStudents(allStudentsList);
    });
    
    return () => unsubscribe();
  }, []);

  // Registration Form View
  if (currentView === 'registration-form' && selectedProgram) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
        {/* White Header */}
        <div style={{ 
          background: 'white',
          borderBottom: '1px solid #e5e7eb',
          padding: '1.5rem 0'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <img src="/images/logo.png" alt="Stemtree" style={{ height: 'clamp(40px, 10vw, 60px)', flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <h1 style={{ margin: 0, fontSize: 'clamp(1rem, 4vw, 1.75rem)', fontWeight: 'bold', color: '#111827' }}>
                Stemtree of Redmond
              </h1>
              <p style={{ 
                margin: '0.25rem 0 0 0', 
                color: '#6b7280', 
                fontSize: 'clamp(0.65rem, 2.5vw, 0.875rem)',
                lineHeight: '1.3'
              }}>
                {programInfo.address} • {programInfo.phone}
              </p>
            </div>
          </div>
        </div>

        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem' }}>
          <button 
            style={{...styles.backButton, backgroundColor: '#3b82f6', color: 'white', marginBottom: '2rem'}}
            onClick={() => setCurrentView('program-details')}
          >
            ← Back to Program Details
          </button>

       {/* Parent Notice */}
          <div style={{ 
            backgroundColor: '#e0f2fe', 
            border: '2px solid #00A3E0', 
            borderRadius: '12px', 
            padding: '1.5rem',
            marginBottom: '2rem'
          }}>
            <h3 style={{ margin: '0 0 0.75rem 0', color: '#0c4a6e', fontSize: '1.125rem', fontWeight: 'bold' }}>
              📋 Important: Online Registration Update
            </h3>
            <p style={{ margin: 0, color: '#0c4a6e', lineHeight: '1.6', fontSize: '0.9375rem' }}>
              We're moving to online forms to keep student information up-to-date and accessible. Please complete one form per student enrolled in any Stemtree program. If you've previously submitted a paper form, please complete this online version. You only need to resubmit if your information changes or once a year.
            </p>
          </div>

          {/* BH Lookup Section - Only for third-party-billing */}
          {selectedProgram.id === 'third-party-billing' && !registrationData._bhPreFilled && (
            <div style={{ 
              ...styles.card, 
              marginBottom: '2rem',
              backgroundColor: '#f0f9ff',
              border: '2px solid #00A3E0'
            }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: 'bold', color: '#0c4a6e', marginBottom: '1rem' }}>
                Bright Horizons Authorization
              </h3>
              <p style={{ fontSize: '0.875rem', color: '#0c4a6e', marginBottom: '1.5rem' }}>
                This form is for Full-Day Camp registrations through Bright Horizons corporate partners only.
              </p>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '1rem', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="bhStatus"
                    value="yes"
                    checked={registrationData._bhLookupChoice === 'yes'}
                    onChange={(e) => setRegistrationData({...registrationData, _bhLookupChoice: 'yes', _bhLookupError: ''})}
                    style={{ marginRight: '0.75rem', marginTop: '0.25rem', width: '18px', height: '18px' }}
                  />
                  <span style={{ fontSize: '0.9375rem', color: '#0c4a6e' }}>
                    <strong>Yes</strong> - I received an authorization email from Bright Horizons
                  </span>
                </label>

                {registrationData._bhLookupChoice === 'yes' && (
                  <div style={{ marginLeft: '2rem', marginTop: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#0c4a6e', marginBottom: '0.5rem' }}>
                      Enter your email address:
                    </label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <input
                        type="email"
                        value={registrationData._bhLookupEmail || ''}
                        onChange={(e) => setRegistrationData({...registrationData, _bhLookupEmail: e.target.value})}
                        placeholder="your.email@example.com"
                        style={{
                          flex: 1,
                          padding: '0.75rem',
                          border: '2px solid #bae6fd',
                          borderRadius: '8px',
                          fontSize: '1rem'
                        }}
                      />
                      <button
                        type="button"
                        onClick={async () => {
                          const email = registrationData._bhLookupEmail?.trim();
                          if (!email) {
                            setRegistrationData({...registrationData, _bhLookupError: 'Please enter your email address'});
                            return;
                          }

                          try {
                            const { collection, query, where, getDocs } = await import('firebase/firestore');
                            const studentsRef = collection(db, 'students');
                            const q = query(
                              studentsRef,
                              where('parentEmail', '==', email),
                              where('billingType', '==', 'bright-horizons')
                            );
                            const querySnapshot = await getDocs(q);
if (!querySnapshot.empty) {
  const students = querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));

  // If multiple students found, show selection
  if (students.length > 1) {
    const studentList = students.map((s, i) => 
      `${i + 1}. ${s.firstName} ${s.lastName} - Camp: ${s.campStartDate || 'N/A'} to ${s.campEndDate || 'N/A'}`
    ).join('\n');
    
    const selection = prompt(
      `Found ${students.length} registrations for this email:\n\n${studentList}\n\nEnter the number of the registration you want to update (1-${students.length}):`
    );
    
    const selectedIndex = parseInt(selection) - 1;
    if (selectedIndex < 0 || selectedIndex >= students.length || isNaN(selectedIndex)) {
      setRegistrationData({...registrationData, _bhLookupError: 'Invalid selection'});
      return;
    }
    
    var studentData = students[selectedIndex];
    var studentId = students[selectedIndex].id;
  } 

  // Convert MM-DD-YYYY to yyyy-MM-dd for HTML date inputs
  const convertToDateInputFormat = (dateStr) => {
    if (!dateStr) return '';
    try {
      // Handle MM-DD-YYYY format
      if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
        const [month, day, year] = dateStr.split('-');
        return `${year}-${month}-${day}`;
      }
      // Handle other formats
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const dayNum = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${dayNum}`;
    } catch {
      return '';
    }
  };

  // Pre-fill form with BH data
  setRegistrationData({
    ...registrationData,
    studentFirstName: studentData.firstName || '',
    studentLastName: studentData.lastName || '',
    dateOfBirth: convertToDateInputFormat(studentData.dateOfBirth),
    gender: studentData.gender || '',
    grade: studentData.grade || '',
    parentFirstName: studentData.parentName?.split(' ')[0] || '',
    parentLastName: studentData.parentName?.split(' ').slice(1).join(' ') || '',
    parentEmail: studentData.parentEmail || '',
    parentPhone: studentData.parentPhone || '',
    homeAddress: studentData.homeAddress || '',
    medicalConditionsType: studentData.medicalConditions !== 'None' ? 'Other' : 'None',
    medicalConditions: studentData.medicalConditions || '',
    authorizedPickup: studentData.authorizedPickup || '',
    campStartDate: convertToDateInputFormat(studentData.campStartDate),
    campEndDate: convertToDateInputFormat(studentData.campEndDate),
    _bhStudentId: studentId,
    _bhPreFilled: true,
    _bhLookupError: '',
    _bhMultipleRecords: students.length > 1
  });

  alert(`✅ Found your registration! Form has been pre-filled.\n\n${students.length > 1 ? 'Note: You have multiple BH registrations. You selected:\n' + studentData.firstName + ' ' + studentData.lastName + ' (' + (studentData.campStartDate || 'N/A') + ')' : 'Your Bright Horizons information has been loaded.'}`);
} else {
  setRegistrationData({
    ...registrationData, 
    _bhLookupError: 'No Bright Horizons authorization found for this email. Please make sure you\'ve received your authorization email from Bright Horizons first.'
  });
}
                          } catch (error) {
                            console.error('BH lookup error:', error);
                            setRegistrationData({...registrationData, _bhLookupError: 'Error searching for registration. Please try again.'});
                          }
                        }}
                        style={{
                          padding: '0.75rem 1.5rem',
                          backgroundColor: '#00A3E0',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '1rem',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Search
                      </button>
                    </div>
                    {registrationData._bhLookupError && (
                      <p style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                        {registrationData._bhLookupError}
                      </p>
                    )}
                  </div>
                )}

                <label style={{ display: 'flex', alignItems: 'flex-start', marginTop: '1rem', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="bhStatus"
                    value="no"
                    checked={registrationData._bhLookupChoice === 'no'}
                    onChange={(e) => setRegistrationData({...registrationData, _bhLookupChoice: 'no'})}
                    style={{ marginRight: '0.75rem', marginTop: '0.25rem', width: '18px', height: '18px' }}
                  />
                  <span style={{ fontSize: '0.9375rem', color: '#0c4a6e' }}>
                    <strong>No</strong> - I haven't registered through Bright Horizons yet
                  </span>
                </label>

                {registrationData._bhLookupChoice === 'no' && (
                  <div style={{ marginLeft: '2rem', marginTop: '1rem', padding: '1rem', backgroundColor: '#fef3c7', borderRadius: '8px' }}>
                    <p style={{ fontSize: '0.875rem', color: '#92400e', marginBottom: '0.75rem' }}>
                      Please register with Bright Horizons first. They will send you an authorization email.
                    </p>
                    <p style={{ fontSize: '0.875rem', color: '#92400e', marginBottom: '0.75rem' }}>
                      Want to pay directly instead?
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setCurrentView('program-details');
                        setSelectedProgram({
                          id: 'full-day-camp',
                          name: 'Full-Day Camp',
                          subtitle: '9am-4pm • Summer/Fall/Winter',
                          description: 'Full day camp for grades K-8 with Science, Coding, Robotics, Math. One-on-one instruction, customized learning.',
                          duration: 'For Kirkland location - Book here:https://stemtree.com/kirklandwa/registration/',
                          extendedCare: 'Extended care until 5pm available',
                          price: 'From $119/day',
                          needsPickup: false,
                          image: '/images/camp.png'
                        });
                      }}
                      style={{
                        padding: '0.75rem 1.5rem',
                        backgroundColor: '#ee6123',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '0.9375rem',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Register for Full-Day Camp →
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

        <div style={styles.card}>
            <form onSubmit={async (e) => {
              e.preventDefault();
              
              if (!registrationData.studentFirstName || !registrationData.studentLastName) {
                alert('Please enter student name');
                return;
              }
              
              if (!registrationData.parentEmail || !registrationData.parentPhone) {
                alert('Please enter parent contact information');
                return;
              }
              
              if (selectedProgram.needsPickup && registrationData.pickupDays.length === 0) {
                alert('Please select pickup days');
                return;
              }
              
              if ((selectedProgram.id === 'after-school-dropoff' || selectedProgram.id === 'after-school-pickup' || selectedProgram.id === 'full-day-camp') && !registrationData.programDaysPerWeek) {
                alert('Please select duration/days per week');
                return;
              }
              
              if (!registrationData.agreementAccepted) {
                alert('Please accept the Statement of Fees and Policy');
                return;
              }

              if (selectedProgram.id === 'birthday-parties') {
                if (!registrationData.partyDate) {
                  alert('Please select a party date');
                  return;
                }
                if (!registrationData.partyTime) {
                  alert('Please select a party time');
                  return;
                }
                if (registrationData.numberOfChildren < 10 || registrationData.numberOfChildren > 20) {
                  alert('Number of children must be between 10 and 20');
                  return;
                }
              }

            // Third-party billing submission
              if (selectedProgram.id === 'third-party-billing') {
                try {
                  const response = await fetch('https://us-central1-stemtree-pickup-app.cloudfunctions.net/submitThirdPartyRegistration', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(filterRegistrationData(registrationData, selectedProgram.id))
                  });

                  if (!response.ok) {
                    throw new Error('Submission failed');
                  }

                  alert('✅ Registration submitted! You will receive a confirmation email shortly.');
                  setCurrentView('public-register');
                  setSelectedProgram(null);
                  
                  // Reset form
                  setRegistrationData({
                    studentFirstName: '',
                    studentLastName: '',
                    grade: '',
                    dateOfBirth: '',
                    gender: '',
                    learningDisabilities: 'None',
                    learningDisabilitiesOther: '',
                    medicalConditionsType: 'None',
                    medicalConditions: '',
                    programDaysPerWeek: '',
                    startDate: '',
                    parentFirstName: '',
                    parentLastName: '',
                    parentEmail: '',
                    parentPhone: '',
                    relationshipToStudent: '',
                    homeAddress: '',
                    schoolName: '',
                    schoolAddress: '',
                    schoolPhone: '',
                    dismissalTimeNormal: '15:15',
                    dismissalTimeWednesday: '14:15',
                    pickupDays: [],
                    campStartDate: '',
                    campEndDate: '',
                    authorizedPickup: '',
                    photoRelease: '',
                    agreementAccepted: false,
                    parentSignature: '',
                    partyDate: '',
                    partyTime: '13:00',
                    numberOfChildren: 10,
                    partyTheme: 'Science Workouts',
                    photoReleaseParty: false
                  });
                } catch (error) {
                  console.error('❌ Registration error:', error);
                  alert(`❌ Registration failed: ${error.message}\n\nPlease contact us at (425) 559-7836 or redmond@stemtree.com`);
                }
                return;
              }

// Birthday Parties - Dynamic Stripe Checkout
              if (selectedProgram.id === 'birthday-parties') {
                try {
                  const numberOfChildren = registrationData.numberOfChildren;
                  let price = numberOfChildren <= 10 ? 299 : 
                              numberOfChildren <= 15 ? 400 : 
                              400 + (25 * (numberOfChildren - 15));

                  const registrationDoc = await addDoc(collection(db, 'pendingRegistrations'), {
                    ...filterRegistrationData(registrationData, selectedProgram.id),
                    programId: selectedProgram.id,
                    programName: selectedProgram.name,
                    needsPickup: false,
                    paymentStatus: 'pending',
                    createdAt: new Date(),
                    partyDate: registrationData.partyDate,
                    partyTime: registrationData.partyTime,
                    numberOfChildren: registrationData.numberOfChildren,
                    partyTheme: registrationData.partyTheme,
                    photoRelease: registrationData.photoReleaseParty,
                    totalPrice: price
                  });

                  const response = await fetch('https://us-central1-stemtree-pickup-app.cloudfunctions.net/createBirthdayPartyCheckout', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      registrationId: registrationDoc.id,
                      amount: price * 100,
                      childName: `${registrationData.studentFirstName} ${registrationData.studentLastName}`,
                      partyDate: registrationData.partyDate,
                      numberOfChildren: registrationData.numberOfChildren,
                      parentEmail: registrationData.parentEmail
                    })
                  });

                  if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(`Cloud Function failed: ${errorText}`);
                  }

                  const responseData = await response.json();
                  const { sessionUrl } = responseData;
                  
                  if (!sessionUrl) {
                    throw new Error('No session URL returned');
                  }

                  window.location.href = sessionUrl;

                } catch (error) {
                  console.error('❌ Birthday party checkout error:', error);
                  alert(`Error: ${error.message}\n\nPlease check the console for details.`);
                }
                return;
              }

              // Normal Stripe payment flow
              let stripeUrl = null;

              if (selectedProgram.id === 'saturday-classes') {
                stripeUrl = stripePaymentLinks['saturday-classes']['monthly'];
              } else if (selectedProgram.id === 'after-school-pickup') {
                stripeUrl = stripePaymentLinks['after-school-pickup'][registrationData.programDaysPerWeek];
              } else if (selectedProgram.id === 'after-school-dropoff') {
                stripeUrl = stripePaymentLinks['after-school-dropoff'][registrationData.programDaysPerWeek];
              } else if (selectedProgram.id === 'full-day-camp') {
                stripeUrl = stripePaymentLinks['full-day-camp'][registrationData.programDaysPerWeek];
              }

              if (stripeUrl) {
                try {
                  const registrationDoc = await addDoc(collection(db, 'pendingRegistrations'), {
                  ...filterRegistrationData({
                    ...registrationData,
                    programSelection: selectedProgram.name  // ← Add this line
                  }, selectedProgram.id),
                  programId: selectedProgram.id,
                  programName: selectedProgram.name,
                  needsPickup: selectedProgram.needsPickup,
                  paymentStatus: 'pending',
                  createdAt: new Date()
                });
                  
                  window.location.href = `${stripeUrl}?client_reference_id=${registrationDoc.id}`;
                } catch (error) {
                  console.error('Error saving registration:', error);
                  alert('Error saving registration. Please try again.');
                }
              } else {
                alert('Error: Could not determine payment link. Please contact support.');
              }
            }}>

{/* Returning Student Check - ENHANCED */}
<div style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #3b82f6' }}>
  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
    <input
      type="checkbox"
      style={{ marginRight: '0.5rem', width: '18px', height: '18px' }}
      checked={registrationData._isReturningCustomer || false}
      onChange={(e) => {
        setRegistrationData({
          ...registrationData, 
          _isReturningCustomer: e.target.checked,
          _lookupEmail: '',
          _lookupError: ''
        });
      }}
    />
    <span style={{ fontWeight: '500', color: '#1e40af' }}>
      I'm a returning student (auto-fill my information)
    </span>
  </label>

  {registrationData._isReturningCustomer && (
    <div style={{ marginTop: '1rem', marginLeft: '2rem' }}>
      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#1e40af', marginBottom: '0.5rem' }}>
        Enter parent email address to find your records:
      </label>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <input
          type="email"
          value={registrationData._lookupEmail || ''}
          onChange={(e) => setRegistrationData({...registrationData, _lookupEmail: e.target.value, _lookupError: ''})}
          placeholder="parent@example.com"
          style={{
            flex: 1,
            padding: '0.75rem',
            border: '2px solid #bae6fd',
            borderRadius: '8px',
            fontSize: '1rem'
          }}
        />
        <button
          type="button"
          onClick={async () => {
            const email = registrationData._lookupEmail?.trim();
            if (!email) {
              setRegistrationData({...registrationData, _lookupError: 'Please enter your email address'});
              return;
            }

            try {
              const { collection, query, where, getDocs } = await import('firebase/firestore');
              const studentsRef = collection(db, 'students');
              
              // Search for students with this parent email
              const q = query(
                studentsRef,
                where('parentEmail', '==', email),
                where('archived', '==', false)
              );
              const querySnapshot = await getDocs(q);

              if (!querySnapshot.empty) {
                const students = querySnapshot.docs.map(doc => ({
                  id: doc.id,
                  ...doc.data()
                }));

                // If multiple students, let user choose
                if (students.length > 1) {
                  const studentList = students.map((s, i) => 
                    `${i + 1}. ${s.firstName} ${s.lastName} - ${s.programSelection || 'N/A'}`
                  ).join('\n');
                  
                  const selection = prompt(
                    `Found ${students.length} students for this email:\n\n${studentList}\n\nEnter the number of the student to update (1-${students.length}):`
                  );
                  
                  const selectedIndex = parseInt(selection) - 1;
                  if (selectedIndex < 0 || selectedIndex >= students.length || isNaN(selectedIndex)) {
                    setRegistrationData({...registrationData, _lookupError: 'Invalid selection'});
                    return;
                  }
                  
                  var studentData = students[selectedIndex];
                  var studentId = students[selectedIndex].id;
                } 

                // Convert date format helper
                const convertToDateInputFormat = (dateStr) => {
                  if (!dateStr) return '';
                  try {
                    // Handle MM-DD-YYYY format
                    if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
                      const [month, day, year] = dateStr.split('-');
                      return `${year}-${month}-${day}`;
                    }
                    // Handle other formats
                    const date = new Date(dateStr);
                    if (isNaN(date.getTime())) return '';
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const dayNum = String(date.getDate()).padStart(2, '0');
                    return `${year}-${month}-${dayNum}`;
                  } catch {
                    return '';
                  }
                };

                // Pre-fill form with existing student data
                setRegistrationData({
                  ...registrationData,
                  studentFirstName: studentData.firstName || '',
                  studentLastName: studentData.lastName || '',
                  dateOfBirth: convertToDateInputFormat(studentData.dateOfBirth),
                  gender: studentData.gender || '',
                  grade: studentData.grade || '',
                  parentFirstName: studentData.parentName?.split(' ')[0] || '',
                  parentLastName: studentData.parentName?.split(' ').slice(1).join(' ') || '',
                  parentEmail: studentData.parentEmail || '',
                  parentPhone: studentData.parentPhone || '',
                  relationshipToStudent: studentData.relationshipToStudent || 'Parent',
                  homeAddress: studentData.homeAddress || '',
                  schoolName: studentData.schoolName || '',
                  schoolAddress: studentData.schoolAddress || '',
                  schoolPhone: studentData.schoolPhone || '',
                  dismissalTimeNormal: studentData.dismissalTimeNormal || '15:15',
                  dismissalTimeWednesday: studentData.dismissalTimeWednesday || '14:15',
                  medicalConditionsType: studentData.medicalConditions && studentData.medicalConditions !== 'None' ? 'Other' : 'None',
                  medicalConditions: studentData.medicalConditions || '',
                  learningDisabilities: studentData.learningDisabilities || 'None',
                  learningDisabilitiesOther: studentData.learningDisabilitiesOther || '',
                  authorizedPickup: studentData.authorizedPickup || '',
                  photoRelease: studentData.photoRelease || '',
                  // Store student ID for update (not create)
                  _existingStudentId: studentId,
                  _isReturningCustomer: true,
                  _lookupError: '',
                  _lookupEmail: email
                });

                alert(`✅ Found your information!\n\nStudent: ${studentData.firstName} ${studentData.lastName}\n\nYour form has been pre-filled. Please review and update as needed.`);
              } else {
                setRegistrationData({
                  ...registrationData, 
                  _lookupError: 'No students found with this email address. Please check your email or fill out the form as a new student.'
                });
              }
            } catch (error) {
              console.error('Lookup error:', error);
              setRegistrationData({...registrationData, _lookupError: 'Error searching for records. Please try again.'});
            }
          }}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Find My Info
        </button>
      </div>
      {registrationData._lookupError && (
        <p style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.5rem' }}>
          {registrationData._lookupError}
        </p>
      )}
    </div>
  )}
</div>

              <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '1rem' }}>
                Student Information
              </h2>

              <div style={styles.gridTwoCols}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                    First Name *
                  </label>
                  <input
                    type="text"
                    style={styles.input}
                    value={registrationData.studentFirstName}
                    onChange={(e) => setRegistrationData({...registrationData, studentFirstName: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                    Last Name *
                  </label>
                  <input
                    type="text"
                    style={styles.input}
                    value={registrationData.studentLastName}
                    onChange={(e) => setRegistrationData({...registrationData, studentLastName: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div style={styles.gridTwoCols}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                    Grade *
                  </label>
                  <select
                    style={styles.select}
                    value={registrationData.grade}
                    onChange={(e) => setRegistrationData({...registrationData, grade: e.target.value})}
                    required
                  >
                    <option value="">Please Select</option>
                    <option value="Pre-K">Pre-K</option>
                    <option value="K">K</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>
                    <option value="6">6</option>
                    <option value="7">7</option>
                    <option value="8">8</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                    Date of Birth *
                  </label>
                  <input
                    type="date"
                    style={styles.input}
                    value={registrationData.dateOfBirth}
                    onChange={(e) => setRegistrationData({...registrationData, dateOfBirth: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div style={styles.gridTwoCols}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                    Gender *
                  </label>
                  <select
                    style={styles.select}
                    value={registrationData.gender}
                    onChange={(e) => setRegistrationData({...registrationData, gender: e.target.value})}
                    required
                  >
                    <option value="">Please Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                    Learning Disabilities
                  </label>
                  <select
                    style={styles.select}
                    value={registrationData.learningDisabilities}
                    onChange={(e) => setRegistrationData({...registrationData, learningDisabilities: e.target.value})}
                  >
                    <option value="None">None</option>
                    <option value="ADD">ADD</option>
                    <option value="ADHD">ADHD</option>
                    <option value="Auditory Processing">Auditory Processing</option>
                    <option value="Visual Processing">Visual Processing</option>
                    <option value="Dyslexia">Dyslexia</option>
                    <option value="Dyscalculia">Dyscalculia</option>
                    <option value="Dysgraphia">Dysgraphia</option>
                    <option value="Other">Other</option>
                  </select>
                  
                  {registrationData.learningDisabilities === 'Other' && (
                    <input
                      type="text"
                      style={{...styles.input, marginTop: '0.5rem'}}
                      placeholder="Please specify"
                      value={registrationData.learningDisabilitiesOther || ''}
                      onChange={(e) => setRegistrationData({...registrationData, learningDisabilitiesOther: e.target.value})}
                    />
                  )}
                </div>
              </div>

              <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1f2937', marginTop: '2rem', marginBottom: '1rem' }}>
                Medical & Safety Information
              </h2>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                  Medical Conditions
                </label>
                <select
                  style={styles.select}
                  value={registrationData.medicalConditionsType}
                  onChange={(e) => setRegistrationData({...registrationData, medicalConditionsType: e.target.value})}
                >
                  <option value="None">None</option>
                  <option value="Asthma">Asthma</option>
                  <option value="Allergies">Allergies</option>
                  <option value="Other">Other</option>
                </select>
                
                {registrationData.medicalConditionsType !== 'None' && (
                  <input
                    type="text"
                    style={{...styles.input, marginTop: '0.5rem'}}
                    value={registrationData.medicalConditions}
                    onChange={(e) => setRegistrationData({...registrationData, medicalConditions: e.target.value})}
                    placeholder="Please provide details"
                  />
                )}
              </div>

              <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1f2937', marginTop: '2rem', marginBottom: '1rem' }}>
                Program Selection
              </h2>

              <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#f0f9ff', borderRadius: '8px' }}>
                <div style={{ fontWeight: '600', color: '#1e40af', marginBottom: '0.5rem' }}>Selected Program:</div>
                <div style={{ color: '#3b82f6' }}>{selectedProgram.name}</div>
              </div>

              {(selectedProgram.id === 'after-school-dropoff' || selectedProgram.id === 'after-school-pickup' || selectedProgram.id === 'full-day-camp') && (
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                    {selectedProgram.id === 'full-day-camp' ? 'Camp Duration *' : 'How many days per week? *'}
                  </label>
                  <select
                    style={styles.select}
                    value={registrationData.programDaysPerWeek}
                    onChange={(e) => setRegistrationData({...registrationData, programDaysPerWeek: e.target.value, pickupDays: []})}
                    required
                  >
                    <option value="">Please Select</option>
                    {selectedProgram.id === 'full-day-camp' ? (
                      <>
                        <option value="1-day">1 Day</option>
                        <option value="2-days">2 Days</option>
                        <option value="3-days">3 Days</option>
                        <option value="4-days">4 Days</option>
                        <option value="1-week">1 Week (5 days)</option>
                        <option value="2-weeks">2 Weeks (10 days)</option>
                      </>
                    ) : (
                      <>
                        <option value="1">1 day/week</option>
                        <option value="2">2 days/week</option>
                        <option value="3">3 days/week</option>
                        <option value="4">4 days/week</option>
                        <option value="5">5 days/week</option>
                      </>
                    )}
                  </select>
                </div>
              )}

              {selectedProgram.id === 'full-day-camp' && registrationData.programDaysPerWeek && (
                <div style={{ marginBottom: '1rem' }}>
                  {registrationData.programDaysPerWeek === '1-day' ? (
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                        Camp Date *
                      </label>
                      <input
                        type="date"
                        style={styles.input}
                        value={registrationData.campStartDate}
                        onChange={(e) => setRegistrationData({
                          ...registrationData, 
                          campStartDate: e.target.value,
                          campEndDate: e.target.value
                        })}
                        required
                      />
                    </div>
                  ) : (
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                        Camp Start Date *
                      </label>
                      <input
                        type="date"
                        style={styles.input}
                        value={registrationData.campStartDate}
                        onChange={(e) => {
                          const start = new Date(e.target.value);
                          const duration = registrationData.programDaysPerWeek === '2-days' ? 1 
                                         : registrationData.programDaysPerWeek === '3-days' ? 2
                                         : registrationData.programDaysPerWeek === '4-days' ? 3
                                         : 4;
                          const end = new Date(start);
                          end.setDate(start.getDate() + duration);
                          
                          setRegistrationData({
                            ...registrationData,
                            campStartDate: e.target.value,
                            campEndDate: end.toISOString().split('T')[0]
                          });
                        }}
                        required
                      />
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginTop: '1rem', marginBottom: '0.25rem' }}>
                        Camp End Date (Auto-calculated)
                      </label>
                      <input
                        type="date"
                        style={{...styles.input, backgroundColor: '#f3f4f6', cursor: 'not-allowed'}}
                        value={registrationData.campEndDate || ''}
                        readOnly
                      />
                    </div>
                  )}
                </div>
              )}

              {selectedProgram.id !== 'full-day-camp' && selectedProgram.id !== 'birthday-parties' && (
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                    Start Date *
                  </label>
                  <input
                    type="date"
                    style={styles.input}
                    value={registrationData.startDate}
                    onChange={(e) => setRegistrationData({...registrationData, startDate: e.target.value})}
                    required
                  />
                </div>
              )}

              {selectedProgram.id === 'birthday-parties' && (
                <div style={{ marginBottom: '2rem' }}>
                  <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '1rem' }}>
                    🎉 Party Details
                  </h2>

                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                    gap: '1rem',
                    marginBottom: '1rem'
                  }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                        Party Date *
                      </label>
                      <input
                        type="date"
                        style={styles.input}
                        value={registrationData.partyDate}
                        onChange={(e) => setRegistrationData({...registrationData, partyDate: e.target.value})}
                        required
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                        Party Time *
                      </label>
                      <input
                        type="time"
                        style={styles.input}
                        value={registrationData.partyTime}
                        onChange={(e) => setRegistrationData({...registrationData, partyTime: e.target.value})}
                        required
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                      Number of Children (including birthday child) *
                    </label>
                    <select
                      style={styles.select}
                      value={registrationData.numberOfChildren}
                      onChange={(e) => setRegistrationData({...registrationData, numberOfChildren: parseInt(e.target.value)})}
                      required
                    >
                      {[...Array(11)].map((_, i) => {
                        const count = i + 10;
                        let price = count <= 10 ? 299 : count <= 15 ? 400 : 400 + (25 * (count - 15));
                        return (
                          <option key={count} value={count}>
                            {count} children - ${price}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                      Party Theme *
                    </label>
                    <select
                      style={styles.select}
                      value={registrationData.partyTheme}
                      onChange={(e) => setRegistrationData({...registrationData, partyTheme: e.target.value})}
                      required
                    >
                      <option value="Science Workouts">Science Workouts</option>
                      <option value="Engineering Workouts">Engineering Workouts</option>
                      <option value="Both">Both (Science & Engineering)</option>
                    </select>
                  </div>

                  <div style={{ 
                    padding: '1.25rem', 
                    backgroundColor: '#fef3c7', 
                    border: '2px solid #f59e0b', 
                    borderRadius: '8px',
                    textAlign: 'center',
                    marginTop: '1rem'
                  }}>
                    <p style={{ margin: 0, fontSize: '0.875rem', color: '#92400e', fontWeight: '500' }}>
                      Total Price
                    </p>
                    <p style={{ margin: '0.5rem 0 0 0', fontSize: '2rem', fontWeight: 'bold', color: '#92400e' }}>
                      ${(() => {
                        const count = registrationData.numberOfChildren;
                        if (count <= 10) return 299;
                        if (count <= 15) return 400;
                        return 400 + (25 * (count - 15));
                      })()}
                    </p>
                  </div>
                </div>
              )}

              {selectedProgram.id !== 'saturday-classes' && selectedProgram.id !== 'full-day-camp' && registrationData.programDaysPerWeek && registrationData.programDaysPerWeek !== '5' && (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                    Which days? (Select {registrationData.programDaysPerWeek} day{registrationData.programDaysPerWeek > 1 ? 's' : ''}) *
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.5rem' }}>
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map(day => (
                      <label key={day} style={{ display: 'flex', alignItems: 'center', fontSize: '0.875rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          style={{ marginRight: '0.5rem' }}
                          checked={registrationData.pickupDays.includes(day)}
                          disabled={registrationData.pickupDays.length >= parseInt(registrationData.programDaysPerWeek) && !registrationData.pickupDays.includes(day)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setRegistrationData({
                                ...registrationData,
                                pickupDays: [...registrationData.pickupDays, day]
                              });
                            } else {
                              setRegistrationData({
                                ...registrationData,
                                pickupDays: registrationData.pickupDays.filter(d => d !== day)
                              });
                            }
                          }}
                        />
                        {day}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1f2937', marginTop: '2rem', marginBottom: '1rem' }}>
                Parent/Guardian Information
              </h2>

              <div style={styles.gridTwoCols}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                    Parent First Name *
                  </label>
                  <input
                    type="text"
                    style={styles.input}
                    value={registrationData.parentFirstName}
                    onChange={(e) => setRegistrationData({...registrationData, parentFirstName: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                    Parent Last Name *
                  </label>
                  <input
                    type="text"
                    style={styles.input}
                    value={registrationData.parentLastName}
                    onChange={(e) => setRegistrationData({...registrationData, parentLastName: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div style={styles.gridTwoCols}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    style={styles.input}
                    value={registrationData.parentEmail}
                    onChange={(e) => setRegistrationData({...registrationData, parentEmail: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                    Phone *
                  </label>
                  <input
                    type="tel"
                    style={styles.input}
                    value={registrationData.parentPhone}
                    onChange={(e) => setRegistrationData({...registrationData, parentPhone: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                  Relationship to Student *
                </label>
                <select
                  style={styles.select}
                  value={registrationData.relationshipToStudent}
                  onChange={(e) => setRegistrationData({...registrationData, relationshipToStudent: e.target.value})}
                  required
                >
                  <option value="">Please Select</option>
                  <option value="Parent">Parent</option>
                  <option value="Guardian">Guardian</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                  Authorized Pickup Persons *
                </label>
                <input
                  type="text"
                  style={styles.input}
                  value={registrationData.authorizedPickup}
                  onChange={(e) => setRegistrationData({...registrationData, authorizedPickup: e.target.value})}
                  placeholder="Names and phone numbers of each person authorized to pick up (e.g., Mom, Dad, Grandma Jane)"
                  required
                />
              </div>

              {selectedProgram.needsPickup && (
                <>
                  <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1f2937', marginTop: '2rem', marginBottom: '1rem' }}>
                    School & Pickup Information
                  </h2>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                      School Name *
                    </label>
                    <input
                      type="text"
                      style={styles.input}
                      value={registrationData.schoolName}
                      onChange={(e) => setRegistrationData({...registrationData, schoolName: e.target.value})}
                      required
                    />
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                      School Address *
                    </label>
                    <input
                      type="text"
                      style={styles.input}
                      value={registrationData.schoolAddress}
                      onChange={(e) => setRegistrationData({...registrationData, schoolAddress: e.target.value})}
                      placeholder="Full street address of school"
                      required
                    />
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                      School Phone
                    </label>
                    <input
                      type="tel"
                      style={styles.input}
                      value={registrationData.schoolPhone}
                      onChange={(e) => setRegistrationData({...registrationData, schoolPhone: e.target.value})}
                      placeholder="School office phone number"
                    />
                  </div>

                  <div style={styles.gridTwoCols}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                        Dismissal Time (M, T, Th, F) *
                      </label>
                      <input
                        type="time"
                        style={styles.input}
                        value={registrationData.dismissalTimeNormal}
                        onChange={(e) => setRegistrationData({...registrationData, dismissalTimeNormal: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                        Dismissal Time (Wednesday) *
                      </label>
                      <input
                        type="time"
                        style={styles.input}
                        value={registrationData.dismissalTimeWednesday}
                        onChange={(e) => setRegistrationData({...registrationData, dismissalTimeWednesday: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                </>
              )}

              <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1f2937', marginTop: '2rem', marginBottom: '1rem' }}>
                Agreements
              </h2>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                  Photo Release *
                </label>
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="photoRelease"
                      value="Yes"
                      checked={registrationData.photoRelease === 'Yes'}
                      onChange={(e) => setRegistrationData({...registrationData, photoRelease: e.target.value})}
                      style={{ marginRight: '0.5rem' }}
                      required
                    />
                    <span>Yes - I give permission for photographs taken of my child while participating in the Stemtree programs to be used to communicate to parents and/or in public relations material in the promotion of Stemtree</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="photoRelease"
                      value="No"
                      checked={registrationData.photoRelease === 'No'}
                      onChange={(e) => setRegistrationData({...registrationData, photoRelease: e.target.value})}
                      style={{ marginRight: '0.5rem' }}
                    />
                    <span>No - I do not give permission</span>
                  </label>
                </div>
              </div>

              <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#fef3c7', borderRadius: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={registrationData.agreementAccepted}
                    onChange={(e) => setRegistrationData({...registrationData, agreementAccepted: e.target.checked})}
                    style={{ marginRight: '0.75rem', marginTop: '0.25rem', width: '18px', height: '18px' }}
                    required
                  />
                  <span style={{ fontSize: '0.875rem', color: '#92400e' }}>
                    I have read and understand the{' '}
                    <button
                      style={{ background: 'none', border: 'none', color: '#92400e', fontWeight: 'bold', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}
                      onClick={(e) => {
                        e.preventDefault();
                        alert(`AGREEMENT TERMS\n\nParental Consent: By enrolling my child(ren) in any Stemtree program, I agree to the following:\n\nEducational Focus – Stemtree is an educational center focused on science and engineering. It is not a daycare or babysitting service.\n\nDeposit – The first month's payment acts as a non-refundable deposit to reserve a spot.\n\nPayments – A valid credit card is required and will be auto-charged on the 1st of each month.\n\nCancellation – Written notice is required at least 30 days before the next scheduled monthly payment to cancel participation.\n\nRefunds – No refunds or make-ups are provided for absences due to illness, vacations, or other personal schedule conflicts.\n\nAbsence Notification – For safety, parents must call or email Stemtree before pick-up time if a child will be absent or leaving school early.\n\nFacility Policy – Stemtree is a drop-in educational facility. Children are free to come and go at the direction of their parent/guardian, not at the direction of Stemtree.\n\nFor Pick-Up Programs (only if your child is enrolled in a pick-up program):\n\nProgram Schedule\nSchool Days – Pick-up is provided only on regular school days and on the pre-aligned early dismissal day each week.\n\nNo School Days – There is no pick-up or program service on statutory holidays, professional development days, Spring/Winter/Summer Breaks, inclement weather closures, or any unscheduled school closures. Parents must have a back-up plan for these situations.\n\nParents must promptly inform Stemtree of any absence or change in dismissal time.\n\nParents must provide correct school address, dismissal time, and emergency contact information.`);
                      }}
                    >
                      Statement of Fees and Policy
                    </button>. I give Stemtree of Redmond permission to bill my student's account for the program services indicated on this form. *
                  </span>
                </label>
              </div>

              <div style={{ marginBottom: '2rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                  Parent/Guardian Signature (Type your full name) *
                </label>
                <input
                  type="text"
                  style={styles.input}
                  value={registrationData.parentSignature}
                  onChange={(e) => setRegistrationData({...registrationData, parentSignature: e.target.value})}
                  placeholder="Type your full name as signature"
                  required
                />
              </div>

              <button
                type="submit"
                style={{
                  ...styles.button,
                  width: '100%',
                  padding: '1.25rem',
                  fontSize: '1.125rem',
                  background: 'linear-gradient(135deg, #00A3E0 0%, #ee6123 100%)',
                  border: 'none',
                  color: 'white',
                  fontWeight: 'bold'
                }}
              >
                {selectedProgram.id === 'third-party-billing' ? 'Submit Registration' : 'Continue to Payment'}
              </button>

            </form>
          </div>
        </div>
      </div>
      
    );
  }
    

  // Payment Success View
  if (currentView === 'payment-success') {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
        <div style={{ 
          background: 'white',
          borderBottom: '1px solid #e5e7eb',
          padding: '1.5rem 0'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <img src="/images/logo.png" alt="Stemtree" style={{ height: 'clamp(40px, 10vw, 60px)', flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <h1 style={{ margin: 0, fontSize: 'clamp(1rem, 4vw, 1.75rem)', fontWeight: 'bold', color: '#111827' }}>
                Stemtree of Redmond
              </h1>
              <p style={{ 
                margin: '0.25rem 0 0 0', 
                color: '#6b7280', 
                fontSize: 'clamp(0.65rem, 2.5vw, 0.875rem)',
                lineHeight: '1.3'
              }}>
                {programInfo.address} • {programInfo.phone}
              </p>
            </div>
          </div>
        </div>

        <div style={{ maxWidth: '600px', margin: '0 auto', padding: '2rem' }}>
          <div style={styles.card}>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>✅</div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'linear-gradient(135deg, #00A3E0 0%, #ee6123 100%)', marginBottom: '1rem' }}>
                Payment Successful!
              </h2>
              <p style={{ color: '#6b7280', lineHeight: '1.6' }}>
                Thank you for registering with Stemtree of Redmond. You will receive a confirmation email shortly with all the details.
              </p>
            </div>

            <div style={{ backgroundColor: '#f0f9ff', padding: '1.5rem', borderRadius: '8px', marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: 'bold', color: '#1e40af', marginBottom: '1rem' }}>
                What's Next?
              </h3>
              <ul style={{ color: '#3b82f6', lineHeight: '1.8', margin: 0, paddingLeft: '1.5rem' }}>
                <li>Check your email for confirmation and receipt</li>
                <li>You'll receive program details and start date information</li>
                <li>We'll contact you if we need any additional information</li>
                <li>Your student will be assessed on their first day to create a personalized learning plan</li>
              </ul>
            </div>

            <button
              style={{
                ...styles.button,
                width: '100%',
                padding: '1rem',
                fontSize: '1rem',
                backgroundColor: '#3b82f6'
              }}
              onClick={() => {
                sessionStorage.removeItem('pendingRegistration');
                setCurrentView('public-register');
                setSelectedProgram(null);
                setRegistrationData({
                  studentFirstName: '',
                  studentLastName: '',
                  grade: '',
                  dateOfBirth: '',
                  gender: '',
                  learningDisabilities: 'None',
                  medicalConditionsType: 'None',
                  medicalConditions: '',
                  programDaysPerWeek: '',
                  startDate: '',
                  parentFirstName: '',
                  parentLastName: '',
                  parentEmail: '',
                  parentPhone: '',
                  relationshipToStudent: '',
                  homeAddress: '',
                  schoolName: '',
                  dismissalTimeNormal: '15:15',
                  dismissalTimeWednesday: '14:15',
                  pickupDays: [],
                  campStartDate: '',
                  campEndDate: '',
                  authorizedPickup: '',
                  photoRelease: '',
                  agreementAccepted: false,
                  parentSignature: ''
                });
              }}
            >
              Return to Programs
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleLogout = () => {
    logout();
    setCurrentView('driver-select');
    setSelectedDriver(null);
  };

  const handleBackupAllData = async () => {
    try {
      const confirmed = window.confirm('Create backup of all student data? This will download a JSON file to your computer.');
      if (!confirmed) return;

      const backupData = {
        backupDate: new Date().toISOString(),
        backupType: 'manual',
        students: allStudents,
        statistics: {
          totalStudents: allStudents.length,
          activeStudents: allStudents.filter(s => !s.archived).length,
          archivedStudents: allStudents.filter(s => s.archived).length,
          pickupStudents: allStudents.filter(s => s.needsPickup && !s.archived).length,
          campStudents: allStudents.filter(s => !s.archived && s.programSelection?.includes('Camp')).length
        }
      };

      const jsonString = JSON.stringify(backupData, null, 2);
      
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const filename = `stemtree-backup-${new Date().toISOString().split('T')[0]}.json`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      alert(`✅ Backup created successfully!\n\nFile: ${filename}\n\nStudents backed up: ${allStudents.length}\nActive: ${backupData.statistics.activeStudents}\nArchived: ${backupData.statistics.archivedStudents}\n\n📁 Store this file safely in multiple locations!`);
      
    } catch (error) {
      console.error('Backup error:', error);
      alert('❌ Backup failed. Please try again or contact support.');
    }
  };

  const exportToExcel = (students) => {
    try {
      const exportData = students.map(student => ({
        'Student Name': `${student.firstName} ${student.lastName}`,
        'Grade': student.grade || '-',
        'Program': student.programSelection || '-',
        'Medical Conditions': student.medicalConditions || 'None',
        'Parent Name': student.parentName || '-',
        'Parent Phone': student.parentPhone || '-',
        'Parent Email': student.parentEmail || '-',
        'School': student.schoolName || '-',
        'Pickup Days': student.pickupDays || '-',
        'Form Status': (() => {
          const submissionDate = student.submissionDate?.toDate ? student.submissionDate.toDate() : new Date(student.submissionDate);
          const oneYearAgo = new Date();
          oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
          return submissionDate > oneYearAgo ? 'Valid' : 'Expired';
        })(),
        'Authorized Pickup': student.authorizedPickup || '-'
      }));

      const headers = Object.keys(exportData[0]);
      const csvContent = [
        headers.join(','),
        ...exportData.map(row => headers.map(header => `"${row[header]}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `todays-students-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      alert(`✅ Exported ${students.length} students to CSV file!`);
    } catch (error) {
      console.error('Export error:', error);
      alert('❌ Export failed. Please try again.');
    }
  };

 // PUBLIC ROUTES - Check these BEFORE authentication
  if (currentView === 'bh-waiver') {
    return <BHWaiverForm />;
  }

  if (currentView === 'incident-report-public') {
    return <IncidentReport defaultTab="report" />;
  }

  // LOGIN SCREEN CHECK
  if (!isAuthenticated() && !isPublicRoute) {
    return <LoginForm onLoginSuccess={(role) => {
      if (role === 'admin') {
        setCurrentView('admin-dashboard-new');
      } else if (role === 'staff') {
        setCurrentView('staff-dashboard');
      } else if (role === 'driver1' || role === 'driver2') {
        setSelectedDriver({ id: role, name: role === 'driver1' ? 'Driver 1' : 'Driver 2' });
        setCurrentView('driver-dashboard');
      }
    }} />;
  }

  // Program Details View
  if (currentView === 'program-details' && selectedProgram) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
        <div style={styles.header}>
          <div style={styles.centerContainer}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', maxWidth: '100%' }}>
              <img src="/images/logo.png" alt="Stemtree" style={{ height: '45px', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                <h1 style={{ 
                  ...styles.headerTitle, 
                  fontSize: 'clamp(1.125rem, 4vw, 1.5rem)',
                  margin: '0 0 0.125rem 0'
                }}>
                  Stemtree of Redmond
                </h1>
                <p style={{ 
                  ...styles.headerSubtitle,
                  fontSize: 'clamp(0.7rem, 2.5vw, 0.875rem)',
                  margin: 0
                }}>
                  {programInfo.address} • {programInfo.phone}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div style={styles.centerContainer}>
          <button 
            style={{...styles.backButton, backgroundColor: '#3b82f6', color: 'white', marginBottom: '2rem'}}
            onClick={() => {
              setCurrentView('public-register');
              setSelectedProgram(null);
            }}
          >
            ← Back to All Programs
          </button>

          <div style={styles.card}>
            <div style={{ 
              height: '300px',
              backgroundImage: `url(${selectedProgram.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderRadius: '12px',
              marginBottom: '2rem'
            }}>
            </div>
            
            <h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '1rem' }}>
              {selectedProgram.name}
            </h1>
            
            <div style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '2px solid #3b82f6' }}>
              <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1e40af', margin: 0 }}>
                {selectedProgram.price}
              </p>
              <p style={{ fontSize: '0.875rem', color: '#3b82f6', margin: '0.25rem 0 0 0' }}>
                {selectedProgram.duration}
              </p>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '0.75rem' }}>
                Program Overview
              </h2>
              <p style={{ color: '#4b5563', lineHeight: '1.6', fontSize: '1rem' }}>
                {selectedProgram.description}
              </p>
            </div>

            {/* Pricing Table */}
            <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#f9fafb', borderRadius: '12px', border: '2px solid #e5e7eb' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '1rem' }}>
                💰 Pricing
              </h2>
              
              {selectedProgram.id === 'after-school-pickup' && (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #d1d5db' }}>
                      <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Days per Week</th>
                      <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Monthly Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.75rem', fontSize: '0.9375rem' }}>1 day/week</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.9375rem', fontWeight: '600', color: '#00A3E0' }}>$320/month</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.75rem', fontSize: '0.9375rem' }}>2 days/week</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.9375rem', fontWeight: '600', color: '#00A3E0' }}>$450/month</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.75rem', fontSize: '0.9375rem' }}>3 days/week</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.9375rem', fontWeight: '600', color: '#00A3E0' }}>$600/month</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.75rem', fontSize: '0.9375rem' }}>4 days/week</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.9375rem', fontWeight: '600', color: '#00A3E0' }}>$760/month</td>
                    </tr>
                    <tr style={{ backgroundColor: '#e0f2fe' }}>
                      <td style={{ padding: '0.75rem', fontSize: '0.9375rem', fontWeight: '600' }}>5 days/week</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '1rem', fontWeight: '700', color: '#00A3E0' }}>$920/month</td>
                    </tr>
                  </tbody>
                </table>
              )}

              {selectedProgram.id === 'after-school-dropoff' && (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #d1d5db' }}>
                      <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Days per Week</th>
                      <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Monthly Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.75rem', fontSize: '0.9375rem' }}>1 day/week</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.9375rem', fontWeight: '600', color: '#00A3E0' }}>$160/month</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.75rem', fontSize: '0.9375rem' }}>2 days/week</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.9375rem', fontWeight: '600', color: '#00A3E0' }}>$276/month</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.75rem', fontSize: '0.9375rem' }}>3 days/week</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.9375rem', fontWeight: '600', color: '#00A3E0' }}>$399/month</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.75rem', fontSize: '0.9375rem' }}>4 days/week</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.9375rem', fontWeight: '600', color: '#00A3E0' }}>$530/month</td>
                    </tr>
                    <tr style={{ backgroundColor: '#e0f2fe' }}>
                      <td style={{ padding: '0.75rem', fontSize: '0.9375rem', fontWeight: '600' }}>5 days/week</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '1rem', fontWeight: '700', color: '#00A3E0' }}>$650/month</td>
                    </tr>
                  </tbody>
                </table>
              )}

              {selectedProgram.id === 'saturday-classes' && (
                <div style={{ padding: '1rem', backgroundColor: '#e0f2fe', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#00A3E0', marginBottom: '0.5rem' }}>
                    $160/month
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#0c4a6e' }}>
                    4 weekly sessions • One subject per month
                  </div>
                </div>
              )}

              {selectedProgram.id === 'full-day-camp' && (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #d1d5db' }}>
                      <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Duration</th>
                      <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Total Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.75rem', fontSize: '0.9375rem' }}>1 Day</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.9375rem', fontWeight: '600', color: '#00A3E0' }}>$119</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.75rem', fontSize: '0.9375rem' }}>2 Days</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.9375rem', fontWeight: '600', color: '#00A3E0' }}>$230</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.75rem', fontSize: '0.9375rem' }}>3 Days</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.9375rem', fontWeight: '600', color: '#00A3E0' }}>$350</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.75rem', fontSize: '0.9375rem' }}>4 Days</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.9375rem', fontWeight: '600', color: '#00A3E0' }}>$469</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#fef3c7' }}>
                      <td style={{ padding: '0.75rem', fontSize: '0.9375rem', fontWeight: '600' }}>1 Week (5 days)</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '1rem', fontWeight: '700', color: '#f59e0b' }}>$595</td>
                    </tr>
                    <tr style={{ backgroundColor: '#e0f2fe' }}>
                      <td style={{ padding: '0.75rem', fontSize: '0.9375rem', fontWeight: '600' }}>2 Weeks (10 days)</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '1rem', fontWeight: '700', color: '#00A3E0' }}>$1,149</td>
                    </tr>
                  </tbody>
                </table>
              )}

              <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#fef3c7', borderRadius: '6px', fontSize: '0.8125rem', color: '#92400e' }}>
                💡 <strong>Note:</strong> First month payment acts as non-refundable deposit
              </div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '0.75rem' }}>
                What's Included
              </h2>
              <ul style={{ color: '#4b5563', lineHeight: '1.8', fontSize: '1rem' }}>
                {selectedProgram.id === 'full-day-camp' && (
                  <>
                    <li>Please pack nutfree lunch, snacks and waterbottle for the camp day</li>
                    <li>Science experiments and projects</li>
                    <li>Computer programming (Scratch, Python)</li>
                    <li>Robotics with Lego and Spike kits</li>
                    <li>Mathematics activities</li>
                    <li>One-on-one instruction tailored to each student</li>
                    <li>Extended care available 4-5pm</li>
                  </>
                )}
                {selectedProgram.id === 'saturday-classes' && (
                  <>
                    <li>Focus on one subject per month</li>
                    <li>Scratch Coding, Robotics, Science, Math, or Python</li>
                    <li>Small group instruction</li>
                    <li>Hands-on projects and activities</li>
                    <li>Monthly curriculum that builds skills progressively</li>
                  </>
                )}
                {selectedProgram.id === 'after-school-dropoff' && (
                  <>
                    <li>Science and Math workouts</li>
                    <li>Robotics and Coding activities</li>
                    <li>Homework help and supervision</li>
                    <li>Parent drop-off/pick-up at Stemtree</li>
                    <li>Flexible scheduling 1-5 days per week</li>
                  </>
                )}
                {selectedProgram.id === 'after-school-pickup' && (
                  <>
                    <li>School pickup service included</li>
                    <li>Transportation to Stemtree</li>
                    <li>Science and Math workouts</li>
                    <li>Robotics and Coding activities</li>
                    <li>Homework help and supervised play</li>
                    <li>Snack time (we provide snack)</li>
                    <li>Flexible scheduling 1-5 days per week</li>
                  </>
                )}
              </ul>
            </div>

            {selectedProgram.id === 'birthday-parties' && (
              <>
                <div style={{ marginBottom: '2rem' }}>
                  <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '0.75rem' }}>
                    What's Included
                  </h2>
                  <ul style={{ color: '#4b5563', lineHeight: '1.8', fontSize: '1rem' }}>
                    <li>2-hour party experience (90 min activities + 30 min cake/presents)</li>
                    <li>60 minutes of hands-on STEM activities</li>
                    <li>Choice of Science OR Engineering theme</li>
                    <li>All materials and supplies included</li>
                    <li>Dedicated party instructor</li>
                    <li>Party room setup and cleanup</li>
                    <li>Take-home project for each child</li>
                  </ul>
                </div>

                <div style={{ 
                  marginBottom: '2rem', 
                  padding: '1.5rem', 
                  backgroundColor: '#fef3c7', 
                  borderRadius: '12px',
                  border: '2px solid #fbbf24'
                }}>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: 'bold', color: '#92400e', marginBottom: '1rem' }}>
                    💰 Party Pricing
                  </h3>
                  <div style={{ color: '#92400e', fontSize: '0.9375rem', lineHeight: '1.8' }}>
                    <p style={{ margin: '0.5rem 0' }}>• <strong>$299</strong> for up to 10 children (minimum)</p>
                    <p style={{ margin: '0.5rem 0' }}>• <strong>$399</strong> for 15 children</p>
                    <p style={{ margin: '0.5rem 0' }}>• <strong>$25</strong> per additional child (maximum 20 kids total)</p>
                  </div>
                </div>
              </>
            )}

            <div style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: '#fef3c7', borderRadius: '8px' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 'bold', color: '#92400e', margin: '0 0 0.5rem 0' }}>
                Important Information
              </h3>
              <ul style={{ color: '#92400e', fontSize: '0.875rem', margin: 0, paddingLeft: '1.25rem' }}>
                <li>Registration form required before payment</li>
                <li>All students assessed on first day to create personalized learning plan</li>
                <li>Nut-free facility - please pack a lunch (for full day camp), water bottle and nut-free snacks</li>
                {selectedProgram.needsPickup && <li>Pickup service requires school information during registration</li>}
              </ul>
            </div>

            <button
              style={{
                ...styles.button,
                width: '100%',
                padding: '1.25rem',
                fontSize: '1.125rem',
                background: 'linear-gradient(135deg, #00A3E0 0%, #ee6123 100%)',
                border: 'none',
                color: 'white',
                fontWeight: 'bold'
              }}
              onClick={() => setCurrentView('registration-form')}
            >
              {selectedProgram.id === 'third-party-billing' ? 'Register Here' : 'Register & Pay Now'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Public Registration View
  if (currentView === 'public-register') {
    const programs = [
      {
        id: 'full-day-camp',
        name: 'Full-Day Camp: Summer Camp June 15-Aug 31. Week of June 15th - Redmond "0" spots, Kirkland "4" spots available',
        subtitle: '9am-4pm • Summer/Fall/Winter',
        description: 'Full day camp for grades K-8 with Science, Coding, Robotics, Math. One-on-one instruction, customized learning.',
        duration: 'Camp Time: 9am-4pm, Please do not book before confirming the space availability',
        extendedCare: 'Extended care until 5pm available',
        price: 'From $119/day',
        needsPickup: false
        ,
        image: '/images/camp.png'
      },

      {
        id: 'after-school-pickup',
        name: 'After School Program With Pickup',
        subtitle: 'With School Pickup',
        description: 'Full service with school pickup + enrichment program until 6pm. Science, Math, Robotics, Coding, and supervised play.',
        duration: 'Mon-Fri, Pickup to 6:00 PM',
        price: 'From $320/week',
        needsPickup: true,
        image: '/images/pickup.png'
      },
      
      {
        id: 'after-school-dropoff',
        name: 'After School Enrichment',
        subtitle: 'Drop-off Program',
        description: 'Hands-on after school program 4:00-6:00pm (1hr). Science, Math, Robotics, Coding workouts plus homework help.',
        duration: 'Mon-Fri, 4:00-6:00 PM (1hr)',
        price: 'Flexible scheduling 1-5 days/week',
        needsPickup: false,
        image: '/images/dropoff.png'
      },

      {
        id: 'saturday-classes',
        name: 'Saturday Classes',
        subtitle: 'Monthly Program',
        description: 'Saturday morning STEM classes: Robotics, Python, Scratch Coding, Math, Science. One subject per month.',
        duration: 'Saturdays 9:00-10:00 AM',
        price: 'Monthly registration',
        needsPickup: false,
        image: '/images/saturday.png'
      },
      {
        id: 'birthday-parties',
        name: 'Birthday Parties',
        subtitle: 'STEM-themed Celebrations',
        description: 'Science or Engineering themed birthday parties. 2-hour party includes hands-on activities, experiments, and Pizza!',
        duration: '2 hours (typically 1:00-3:00 PM)',
        price: 'From $299 (10 kids)',
        needsPickup: false,
        image: '/images/birthday.png'
      },
      {
        id: 'third-party-billing',
        name: 'Bright Horizons Registration Form Only',
        subtitle: 'For Bright Horizons & Corporate Partners',
        description: 'Annual registration form for students enrolled through corporate partners. Complete this form once per year or for any change of information to update student information.',
        duration: 'No payment required',
        price: 'Bright Horizon Service Partner',
        needsPickup: false,
        image: '/images/BH.png'
      }
    ];

    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
        {/* White Header */}
        <div style={{ 
          background: 'white',
          borderBottom: '1px solid #e5e7eb',
          padding: '1.5rem 0'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <img src="/images/logo.png" alt="Stemtree" style={{ height: 'clamp(40px, 10vw, 60px)', flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <h1 style={{ margin: 0, fontSize: 'clamp(1rem, 4vw, 1.75rem)', fontWeight: 'bold', color: '#111827' }}>
                Stemtree of Redmond
              </h1>
              <p style={{ 
                margin: '0.25rem 0 0 0', 
                color: '#6b7280', 
                fontSize: 'clamp(0.65rem, 2.5vw, 0.875rem)',
                lineHeight: '1.3'
              }}>
                {programInfo.address} • {programInfo.phone}
              </p>
            </div>
          </div>
        </div>

        {/* Hero Section */}
        <div style={{ 
          background: 'linear-gradient(135deg, #00A3E0 0%, #ee6123 100%)',
          color: 'white',
          padding: '1.5rem 1rem 2.5rem 1rem',
          textAlign: 'center'
        }}>
          <h2 style={{ 
            fontSize: 'clamp(1.5rem, 5vw, 2.5rem)', 
            fontWeight: 'bold', 
            margin: '0 0 0.75rem 0',
            lineHeight: '1.2'
          }}>
            Choose Your Program
          </h2>
          <p style={{ 
            fontSize: 'clamp(0.875rem, 3vw, 1.125rem)', 
            opacity: 0.9, 
            maxWidth: '600px', 
            margin: '0 auto',
            lineHeight: '1.5'
          }}>
            Select a program below to view details and begin registration
          </p>
        </div>

        {/* Program Cards */}
        <div style={{ maxWidth: '1200px', margin: '-1.5rem auto 4rem auto', padding: '0 1rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
            {programs.map(program => (
              <div
                key={program.id}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
                }}
                onClick={() => {
                  setSelectedProgram(program);
                  if (program.id !== 'birthday-parties') {
                    setRegistrationData(prev => ({
                      ...prev,
                      partyDate: '',
                      partyTime: '13:00',
                      numberOfChildren: 10,
                      partyTheme: 'Science Workouts'
                    }));
                  }
                  setCurrentView('program-details');
                }}
              >
                {/* Image Header */}
                <div style={{ 
                  height: '200px',
                  backgroundImage: `url(${program.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  position: 'relative'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '1rem',
                    right: '1rem',
                    backgroundColor: 'rgba(255,255,255,0.95)',
                    padding: '0.5rem 1rem',
                    borderRadius: '20px',
                    fontWeight: 'bold',
                    fontSize: '0.875rem',
                    color: '#00A3E0'
                  }}>
                    {program.price}
                  </div>
                </div>

                {/* Content */}
                <div style={{ padding: '1.5rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <h3 style={{ 
                    fontSize: '1.375rem', 
                    fontWeight: 'bold', 
                    color: '#111827', 
                    margin: '0 0 0.25rem 0' 
                  }}>
                    {program.name}
                  </h3>
                  <p style={{ 
                    color: '#00A3E0', 
                    fontSize: '0.875rem', 
                    fontWeight: '600',
                    margin: '0 0 1rem 0'
                  }}>
                    {program.subtitle}
                  </p>
                  
                  <p style={{ 
                    color: '#6b7280', 
                    fontSize: '0.9375rem', 
                    lineHeight: '1.6',
                    margin: '0 0 1rem 0',
                    flex: 1
                  }}>
                    {program.description}
                  </p>

                  <div style={{
                    padding: '0.75rem',
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px',
                    marginBottom: '1rem'
                  }}>
                    <p style={{ 
                      fontSize: '0.875rem', 
                      color: '#374151',
                      margin: 0,
                      fontWeight: '500'
                    }}>
                      {program.duration}
                    </p>
                    {program.extendedCare && (
                      <p style={{ 
                        fontSize: '0.75rem', 
                        color: '#6b7280',
                        margin: '0.25rem 0 0 0'
                      }}>
                        {program.extendedCare}
                      </p>
                    )}
                  </div>

                  <button
                    style={{
                      width: '100%',
                      padding: '1rem',
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      color: 'white',
                      background: 'linear-gradient(135deg, #00A3E0 0%, #ee6123 100%)',
                      border: 'none',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'transform 0.2s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    View Details →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // DRIVER SELECT VIEW

  if (currentView === 'incident-report' && isPublicRoute) {
  return <IncidentReport defaultTab="report" />;
}

  if (currentView === 'driver-select') {
    if (isAdmin()) {
      setCurrentView('admin-dashboard-new');
      return null;
    }
    
    if (isStaff() && !isAdmin()) {
      return (
        <StaffDashboard 
          onNavigate={(view) => setCurrentView(view)}
          onSelectDriver={(driver) => {
            setSelectedDriver(driver);
            setCurrentView('driver-dashboard');
          }}
        />
      );
    }
    
    if (isDriver()) {
      return (
        <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <h2>Access Restricted</h2>
            <p>Drivers cannot access this view.</p>
            <button style={styles.button} onClick={handleLogout}>
              Return to Login
            </button>
          </div>
        </div>
      );
    }
    
    return (
      <DriverSelect 
        onSelectDriver={(driver) => {
          setSelectedDriver(driver);
          setCurrentView('driver-dashboard');
        }}
        onNavigate={(view) => {
          if (view === 'admin-dashboard-new') {
            setCurrentView('admin-dashboard-new');
          } else {
            setCurrentView(view);
          }
        }}
      />
    );
  }

  // Admin Dashboard
  if (currentView === 'admin-dashboard-new') {
    return (
      <AdminDashboardSidebar
        onNavigate={setCurrentView}
        onLogout={() => {
          localStorage.clear();
          sessionStorage.clear();
          setTimeout(() => {
            window.location.href = '/';
          }, 100);
        }}
        userName={localStorage.getItem('userName')}
      />
    );
  }

  // Staff Dashboard
  if (currentView === 'staff-dashboard') {
    const fromAdmin = isAdmin();
    
    return (
      <StaffDashboard 
        onNavigate={(view) => setCurrentView(view)}
        onSelectDriver={(driver) => {
          setSelectedDriver(driver);
          setCurrentView('driver-dashboard');
        }}
        showBackButton={fromAdmin}
        onBack={() => setCurrentView('admin-dashboard-new')}
      />
    );
  }

  // Today's Roster
  if (currentView === 'todays-students') {
    return (
      <TodaysRoster 
        allStudents={allStudents}
        onBack={() => {
          if (isAdmin()) {
            setCurrentView('admin-dashboard-new');
          } else {
            setCurrentView('staff-dashboard');
          }
        }}
        onNavigate={(view) => setCurrentView(view)}
      />
    );
  }

  // Front Desk
  if (currentView === 'front-desk') {
    return (
      <FrontDesk 
        onBack={() => {
          if (isAdmin()) {
            setCurrentView('admin-dashboard-new');
          } else if (isStaff()) {
            setCurrentView('staff-dashboard');
          }
        }}
      />
    );
  }

  // Archived Students
  if (currentView === 'archived-students') {
    return (
      <ArchivedStudents 
        onBack={() => {
          if (isAdmin()) {
            setCurrentView('admin-dashboard-new');
          } else if (isStaff()) {
            setCurrentView('staff-dashboard');
          } else {
            setCurrentView('driver-select');
          }
        }}
      />
    );
  }

  
   // ...
   if (currentView === 'driver-schedule') {
     return <WeeklyDriverAssignment onBack={() => setCurrentView('all-students')} />;
   }
   
  // All Students
  if (currentView === 'all-students') {
    return (
      <AllStudents 
        onBack={() => {
          if (isAdmin()) {
            setCurrentView('admin-dashboard-new');
          } else if (isStaff()) {
            setCurrentView('staff-dashboard');
          } else {
            setCurrentView('driver-select');
          }
        }}
        onEdit={(student) => {
          setEditingStudent(student);
          setCurrentView('edit-student');
        }}
      />
    );
  }

  // Driver Dashboard
  if (currentView === 'driver-dashboard' && selectedDriver) {
    return (
      <DriverDashboard 
        driver={selectedDriver}
        onBack={() => {
          if (isAdmin()) {
            setCurrentView('admin-dashboard-new');
          } else if (isStaff()) {
            setCurrentView('staff-dashboard');
          } else {
            setCurrentView('driver-select');
          }
        }}
        onLogout={handleLogout}
      />
    );
  }

  // Staff Time Management
  if (currentView === 'staff-time-management') {
    return (
      <StaffManagement 
        onBack={() => setCurrentView('admin-dashboard-new')}
      />
    );
  }

  // Time Reports
  if (currentView === 'time-reports') {
    return (
      <StaffReports 
        onBack={() => setCurrentView('admin-dashboard-new')}
      />
    );
  }

  if (currentView === 'incident-reports') {
    return <IncidentReport defaultTab="protocol" onBack={() => setCurrentView('admin-dashboard-new')} />;
  }

  // Edit Student
  if (currentView === 'edit-student' && editingStudent) {
    return (
      <EditStudent 
        student={editingStudent}
        onBack={() => {
          setEditingStudent(null);
          setCurrentView('all-students');
        }}
        onSuccess={() => {
          setEditingStudent(null);
          setCurrentView('all-students');
        }}
      />
    );
  }

  // Add Student
  if (currentView === 'add-student') {
    return (
      <AddStudent 
        onBack={() => {
          if (isAdmin()) {
            setCurrentView('admin-dashboard-new');
          } else if (isStaff()) {
            setCurrentView('staff-dashboard');
          } else {
            setCurrentView('driver-select');
          }
        }}
      />
    );
  }

  // Settings View
  if (currentView === 'settings') {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #00A3E0 0%, #ee6123 100%)',
          padding: '2rem 0',
          marginBottom: '2rem',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          position: 'relative'
        }}>
          <button
            onClick={handleLogout}
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              padding: '0.625rem 1.25rem',
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(220, 38, 38, 0.3)',
              zIndex: 1000
            }}
          >
            🚪 Logout
          </button>

          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
            <button
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: '2px solid white',
                color: 'white',
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                fontSize: '0.875rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                marginBottom: '1rem'
              }}
              onClick={() => setCurrentView('admin-dashboard-new')}
            >
              ← Back to Admin Dashboard
            </button>
            <h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', margin: 0 }}>
              ⚙️ System Settings
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.9)', margin: '0.5rem 0 0 0' }}>
              System information and configuration
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', borderBottom: '2px solid #e5e7eb' }}>
            <button
              onClick={() => setSettingsTab('overview')}
              style={{
                padding: '1rem 1.5rem',
                backgroundColor: settingsTab === 'overview' ? '#00A3E0' : 'transparent',
                color: settingsTab === 'overview' ? 'white' : '#6b7280',
                border: 'none',
                borderRadius: '8px 8px 0 0',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s'
              }}
            >
              📊 Overview
            </button>
          </div>
        </div>

        {/* Overview Tab */}
        {settingsTab === 'overview' && (
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem 2rem 1rem' }}>
            {/* System Stats */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '1.5rem',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              marginBottom: '1.5rem'
            }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>
                📊 System Statistics
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
                <div>
                  <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Total Students</p>
                  <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3b82f6', margin: 0 }}>
                    {allStudents.filter(s => !s.archived).length}
                  </p>
                </div>
                <div>
                  <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Archived Students</p>
                  <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#6b7280', margin: 0 }}>
                    {allStudents.filter(s => s.archived).length}
                  </p>
                </div>
                <div>
                  <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Pickup Students</p>
                  <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981', margin: 0 }}>
                    {(() => {
                      const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
                      return allStudents.filter(s => {
                        if (s.archived || !s.needsPickup) return false;
                        const pickupDays = s.pickupDays || '';
                        return pickupDays.toLowerCase().includes(today.toLowerCase());
                      }).length;
                    })()}
                  </p>
                </div>
                <div>
                  <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Camp Students</p>
                  <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f59e0b', margin: 0 }}>
                    {allStudents.filter(s => !s.archived && s.programSelection?.includes('Camp')).length}
                  </p>
                </div>
              </div>
            </div>

            {/* System Info */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '1.5rem',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              marginBottom: '1.5rem'
            }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>
                ℹ️ System Information
              </h2>
              <div style={{ fontSize: '0.875rem', color: '#374151', lineHeight: '2' }}>
                <p><strong>Company:</strong> Stemtree of Redmond</p>
                <p><strong>Address:</strong> 17634 NE Union Hill Rd, Redmond, WA 98052</p>
                <p><strong>Phone:</strong> (425) 559-7836</p>
                <p><strong>Email:</strong> redmond@stemtree.com</p>
                <p><strong>Database:</strong> Firebase Firestore</p>
                <p><strong>Hosting:</strong> Firebase Hosting</p>
              </div>
            </div>

            {/* Admin Actions */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '1.5rem',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              marginBottom: '1.5rem'
            }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>
                🔧 Admin Actions
              </h2>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                <button
                  style={{
                    padding: '1rem',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onClick={handleBackupAllData}
                >
                  💾 Backup All Data
                </button>
                <button
                  style={{
                    padding: '1rem',
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onClick={() => exportToExcel(allStudents.filter(s => !s.archived))}
                >
                  📊 Export to CSV
                </button>

                 {/* ADD THIS BUTTON HERE */}
    <button
      style={{
        padding: '1rem',
        backgroundColor: '#ef4444',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '1rem',
        fontWeight: '600',
        cursor: 'pointer',
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}
      onClick={() => setCurrentView('date-migration')}
    >
      ⚠️ Run Date Migration (Staff Time Entries)
    </button>
              </div>
            </div>

            {/* Warning Note */}
            <div style={{
              padding: '1rem',
              backgroundColor: '#fef3c7',
              border: '2px solid #fcd34d',
              borderRadius: '8px'
            }}>
              <p style={{ margin: 0, color: '#92400e', fontSize: '0.875rem' }}>
                ⚠️ <strong>Note:</strong> PIN codes and program settings are configured in the codebase. Contact your developer for any changes to authentication or system constants.
              </p>
            </div>
          </div>
        )}

   
      </div>
    );
  }

  // Driver Status Manager
  if (currentView === 'driver-status-manager') {
    return (
      <DriverStatusManager 
        onBack={() => setCurrentView('admin-dashboard-new')}
      />
    );
  }

  // Staff Check-In
  if (currentView === 'staff-checkin') {
    return <StaffCheckIn />;
  }

  // Staff Management
  if (currentView === 'staff-management') {
    return (
      <StaffManagement 
        onBack={() => {
          if (isAdmin()) {
            setCurrentView('admin-dashboard-new');
          } else {
            setCurrentView('staff-dashboard');
          }
        }}
      />
    );
  }

  // Staff Reports
  if (currentView === 'staff-reports') {
    return (
      <StaffReports 
        onBack={() => {
          if (isAdmin()) {
            setCurrentView('admin-dashboard-new');
          } else {
            setCurrentView('staff-dashboard');
          }
        }}
      />
    );
  }

  // Locations Management
  if (currentView === 'locations') {
    return (
      <LocationsManagement 
        onBack={() => setCurrentView('admin-dashboard-new')}
        onNavigate={setCurrentView}
      />
    );
  }

  // Camp Enrollment Management
  if (currentView === 'camp-enrollment') {
    return (
      <CampEnrollmentManager 
        onBack={() => setCurrentView('admin-dashboard-new')}
      />
    );
  }

  // Staff Schedule
  if (currentView === 'staff-schedule') {
    return (
      <StaffSchedule 
        onBack={() => setCurrentView('admin-dashboard-new')}
        onNavigate={setCurrentView}
      />
    );
  }

  // bhdedup tool

  if (currentView === 'bh-dedup') {
  return (
    <BHDedupTool onBack={() => setCurrentView('admin-dashboard-new')} />
  );
}

  // Payment Tracking
  if (currentView === 'payment-tracking') {
    return (
      <PaymentTracking 
        onBack={() => setCurrentView('admin-dashboard-new')}
      />
    );
  }

    // Pause for Break - ADD THIS NEW SECTION
  if (currentView === 'pause-for-break') {
    return (
      <PauseForBreak 
        onBack={() => setCurrentView('admin-dashboard-new')}
      />
    );
  }



    // Date Migration Tool  ← ADD THIS
  if (currentView === 'date-migration') {
    return (
      <DateMigrationTool 
        onBack={() => setCurrentView('admin-dashboard-new')}
      />
    );
  }
};




// Wrap the app with Context Providers
const App = () => {
  return (
    <AuthProvider>
      <LocationProvider>
        <StudentProvider>
          <PickupManagementApp />
        </StudentProvider>
      </LocationProvider>
    </AuthProvider>
  );
};

export default App;